import { isAnyOf, PayloadAction } from '@reduxjs/toolkit'
import sortBy from 'lodash/sortBy'
import { appListenerMiddleware } from 'reduxTyped'
import { createSelector } from 'reselect'
import { l } from '@manychat/manyui'

import { goalTypes, MAX_GOALS_NUMBER } from 'apps/aiAutomation/lib/goals'
import {
  EngagementGoalSocketEventsSchemas,
  GoalStats,
} from 'apps/aiAutomation/store/engagementGoal/schemas'
import { alert } from 'common/core'
import { mapResponse } from 'shared/api/lib/mapResponse'
import { AiEngagementGoalApi } from 'shared/api/requests/aiEngagementGoal'
import {
  EngagementGoal,
  EngagementGoalStats,
  GoalStatus,
  GoalType,
  LeadQualStats,
} from 'shared/api/requests/aiEngagementGoal/schemas'
import { createListSlice } from 'shared/lib/factory/redux/createListSlice'
import { createListOperations } from 'shared/lib/factory/redux/createListSlice/lib/createListOperations'
import { makeCreateAsyncAction } from 'shared/lib/redux'
import { getClientId } from 'utils/clientId'
import {
  accountNotificationsListener,
  anotherTabNotificationsListener,
} from 'utils/services/notificationsService'

import { DefaultGoalStats } from './lib/constants'

const createAsyncAction = makeCreateAsyncAction({
  scope: 'ai',
  section: 'ai_always_on_automation',
})

const NAMESPACE = 'engagementGoal'

interface ExtraState {
  priorityOrder: string[]
  stats: Record<string, GoalStats>
}

const extraState: ExtraState = {
  priorityOrder: [],
  stats: {},
}

const {
  actions,
  reducer,
  selectors,
  initialState: AiAutomationEngagementGoalInitialState,
} = createListSlice({
  namespace: NAMESPACE,
  storePath: 'aiAutomation.engagementGoal',

  operations: createListOperations({
    getList: mapResponse(AiEngagementGoalApi.list, (response) => response.data.goals),
    getNextList: undefined,
    getPrevList: undefined,
    createItem: mapResponse(
      AiEngagementGoalApi.create,
      (response) => response.data.goal as EngagementGoal,
    ),
    updateItem: mapResponse(
      AiEngagementGoalApi.update,
      (response) => response.data.goal as EngagementGoal,
    ),
    deleteItem: undefined,
  }),

  getItemId: (item) => item.id,

  extraState,
  reducers: {
    priorityOrderUpdated: (state, action: PayloadAction<string[]>) => {
      state.priorityOrder = action.payload
    },
    detailedStatsUpdated: (
      state,
      action: PayloadAction<{ details: LeadQualStats; id: string }>,
    ) => {
      state.stats[action.payload.id] = {
        ...state.stats[action.payload.id],
        details: action.payload.details,
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(actions.getListFx.fulfilled, (state, action) => {
      if (!Array.isArray(action.payload)) {
        return
      }

      state.stats = action.payload.reduce<ExtraState['stats']>((result, goal) => {
        result[goal.id] =
          'stats' in goal && goal.stats ? (goal.stats as EngagementGoalStats) : DefaultGoalStats

        return result
      }, {})

      state.priorityOrder = action.payload
        .filter(({ status }) => status === GoalStatus.ACTIVE)
        .map(({ id }) => id)
    })

    builder.addCase(updatePriorityFx.pending, (state, action) => {
      state.priorityOrder = action.meta.arg
    })

    builder.addCase(updatePriorityFx.rejected, (state) => {
      state.priorityOrder = state.items
        .filter(({ status }) => status === GoalStatus.ACTIVE)
        .map(({ id }) => id)
    })

    builder.addCase(deleteFx.fulfilled, (state, action) => {
      state.priorityOrder = state.priorityOrder.filter((id) => id !== action.meta.arg)
    })

    builder.addMatcher(
      isAnyOf(updateStatusFx.fulfilled.match, updateFx.fulfilled.match),
      (state, action: PayloadAction<EngagementGoal>) => {
        const itWasActive =
          Boolean(state.priorityOrder.find((id) => id === action.payload.id)) &&
          action.payload.status === GoalStatus.ACTIVE

        if (itWasActive) {
          return
        }

        const newOrder = state.priorityOrder.filter((id) => id !== action.payload.id)

        if (action.payload.status === GoalStatus.ACTIVE) {
          newOrder.push(action.payload.id)
        }

        state.priorityOrder = newOrder
      },
    )
  },
})

const createFx = createAsyncAction(
  `${NAMESPACE}/createFx`,
  (goal: EngagementGoal, { dispatch }) => {
    return dispatch(
      actions.createItemFx({ payload: { body: { goal }, query: { client_id: getClientId() } } }),
    ).unwrap()
  },
)

const updateFx = createAsyncAction(
  `${NAMESPACE}/updateFx`,
  async (goal: EngagementGoal, { dispatch }) => {
    return dispatch(
      actions.updateItemFx({
        id: goal.id,
        payload: { body: { goal: goal }, query: { client_id: getClientId() } },
      }),
    ).unwrap()
  },
)

const updateStatusFx = createAsyncAction(
  `${NAMESPACE}/updateStatusFx`,
  async (
    { id, status }: { id: string; status: GoalStatus.ACTIVE | GoalStatus.STOPPED },
    { dispatch, getState },
  ) => {
    const goal = selectors.getItemById(getState(), id)

    const data = await dispatch(
      actions.updateItemFx({
        id: goal.id,
        payload: { body: { goal: { ...goal, status } }, query: { client_id: getClientId() } },
      }),
    ).unwrap()

    alert(
      status === GoalStatus.ACTIVE
        ? l.getString(goalTypes[goal.type].successPublishingAlert)
        : l.translate('Your engagement goal has been stopped'),
      'success',
      {
        icon: 'CheckCircle',
      },
    )

    return data
  },
  {
    condition: ({ id, status }, { getState }) => {
      const goal = selectors.getItemById(getState(), id)

      return goal && goal.status !== status
    },
  },
)

const deleteFx = createAsyncAction(
  `${NAMESPACE}/deleteFx`,
  async (id: string, { dispatch, getState }) => {
    const state = getState()
    const item = selectors.getItemById(state, id)

    if (!item) {
      return
    }

    await dispatch(
      actions.updateItemFx({
        id,
        payload: {
          body: { goal: { ...item, status: GoalStatus.DELETED } },
          query: { client_id: getClientId() },
        },
      }),
    ).unwrap()

    dispatch(actions.deleteItem({ id }))

    alert(l.translate('Your engagement goal has been deleted.'), 'success')
  },
)

const updatePriorityFx = createAsyncAction(
  `${NAMESPACE}/updatePriorityFx`,
  async (ids: string[]) => {
    await AiEngagementGoalApi.updatePriority({
      body: {
        priority: ids,
      },
    })
  },
)

const getStatsFx = createAsyncAction(
  `${NAMESPACE}/getStatsFx`,
  async (id: string, { dispatch, getState }) => {
    const stats = AiAutomationEngagementGoalSelectors.getStatsById(getState() as RootState, id)

    if (stats.details) {
      return
    }

    const { data } = await AiEngagementGoalApi.getStats({
      query: { goal_id: id },
    })

    dispatch(actions.detailedStatsUpdated({ id, details: data }))
  },
)

appListenerMiddleware.startListening({
  matcher: isAnyOf(actions.getListFx.fulfilled, actions.updateItemFx.fulfilled),
  effect: (_, { dispatch, getState }) => {
    const goals = selectors.getList(getState() as RootState)

    goals
      .filter((goal) => goal.status === GoalStatus.ACTIVE && goal.type === GoalType.LEAD_QUAL)
      .forEach(({ id }) => dispatch(AiAutomationEngagementGoalActions.getStatsFx(id)))
  },
})

anotherTabNotificationsListener.on(
  EngagementGoalSocketEventsSchemas.goalCreated,
  (data, dispatch) => {
    dispatch(
      actions.appendItem({
        item: data.goal,
      }),
    )
  },
)

anotherTabNotificationsListener.on(
  EngagementGoalSocketEventsSchemas.goalUpdated,
  (data, dispatch, getState): void => {
    const goal = selectors.getItemById(getState(), data.goal.id)
    const priority = AiAutomationEngagementGoalSelectors.getPriorityOrder(getState() as RootState)

    if (!goal) {
      return
    }

    const priorityWithoutCurrentGoal = priority.filter((id) => id !== data.goal.id)

    if ([GoalStatus.STOPPED, GoalStatus.DELETED].includes(data.goal.status)) {
      dispatch(actions.priorityOrderUpdated(priorityWithoutCurrentGoal))
    } else {
      dispatch(actions.priorityOrderUpdated([...priorityWithoutCurrentGoal, goal.id]))
    }

    if (data.goal.status === GoalStatus.DELETED) {
      dispatch(actions.deleteItem({ id: data.goal.id }))
      return
    }

    dispatch(
      actions.updateItem({
        id: data.goal.id,
        data: data.goal,
        rewrite: true,
      }),
    )
  },
)

anotherTabNotificationsListener.on(
  EngagementGoalSocketEventsSchemas.priorityUpdated,
  (data: { priority: string[] }, dispatch) => {
    dispatch(actions.priorityOrderUpdated(data.priority))
  },
)

accountNotificationsListener.on(
  EngagementGoalSocketEventsSchemas.goalsDeactivated,
  (data: { goal_ids: string[] }, dispatch) => {
    dispatch(
      actions.bulkUpdateItems({
        data: data.goal_ids.map((id) => ({
          id,
          item: {
            status: GoalStatus.STOPPED,
          },
        })),
      }),
    )
  },
)

export const AiAutomationEngagementGoalActions = {
  getListFx: actions.getListFx,
  createFx,
  deleteFx,
  updateFx,
  updateStatusFx,
  updatePriorityFx,
  getStatsFx,
  goalDeleted: actions.deleteItem,

  priorityOrderUpdated: actions.priorityOrderUpdated,
}

const getState = (state: RootState) => state.aiAutomation.engagementGoal
const getPriorityOrder = (state: RootState) => getState(state).priorityOrder
const getStats = (state: RootState) => getState(state).stats
const getActiveGoalsLength = createSelector(
  [selectors.getList],
  (goals) => goals.filter((goal) => goal.status === GoalStatus.ACTIVE).length,
)
const getActiveGoals = createSelector(
  [selectors.getList, getPriorityOrder],
  (goals, priorityOrder) => {
    const activeGoals = goals.filter((goal) => goal.status === GoalStatus.ACTIVE)

    const idToGoalMap = new Map(activeGoals.map((goal) => [goal.id, goal]))

    return priorityOrder
      .map((id) => idToGoalMap.get(id))
      .filter((goal): goal is EngagementGoal => goal !== undefined)
  },
)
const getStoppedGoals = createSelector([selectors.getList], (goals) =>
  goals.filter((goal) => goal.status === GoalStatus.STOPPED),
)

export const AiAutomationEngagementGoalSelectors = {
  ...selectors,
  getPriorityOrder,
  getActiveGoalsLength,
  getIsActiveGoalLimitReached: (state: RootState) =>
    getActiveGoalsLength(state) === MAX_GOALS_NUMBER,
  getActiveGoals,
  getStoppedGoals,

  getSortedGoals: createSelector([getActiveGoals, getStoppedGoals], (activeGoals, stoppedGoals) => {
    return [...activeGoals, ...sortBy(stoppedGoals, (goal) => -goal.ts_updated)]
  }),

  getStatsById: (state: RootState, id: string | null) =>
    id ? { ...DefaultGoalStats, ...getState(state).stats[id] } : DefaultGoalStats,

  getOverviewStats: createSelector([selectors.getList, getStats], (goals, statsMap) => {
    return goals
      .filter((goal) => goal.status === GoalStatus.ACTIVE)
      .reduce<Record<string, number>>((acc, { id, type }) => {
        const stats = id in statsMap ? statsMap[id] : DefaultGoalStats
        if (type === GoalType.LEAD_QUAL) {
          const cufs = stats.details?.cufs ?? []
          const sufs = stats.details?.sufs ?? []
          return {
            ...acc,
            [id]: [...cufs, ...sufs].reduce((sum, { stats }) => sum + stats.saved, 0),
          }
        } else {
          return {
            ...acc,
            [id]: stats.achieved,
          }
        }
      }, {})
  }),
}

export { AiAutomationEngagementGoalInitialState }

export type AiAutomationEngagementGoalState = typeof AiAutomationEngagementGoalInitialState

export const AiAutomationEngagementGoalReducer = reducer
